import { useEffect, useState, CSSProperties } from 'react';
import { getIssueCategories, IssueCategory } from '../services/issueCategories';
import TouchKeyboard from './TouchKeyboard';

export type IssueSeverity = 'info' | 'warning' | 'error';

/**
 * What the modal hands back on submit. The parent decides where it goes:
 * agent POST /api/incidents for PC-scoped marks, direct DB insert for
 * room-scoped marks and unreachable agents.
 */
export interface MarkIssueSubmit {
  category: IssueCategory;
  severity: IssueSeverity;
  note: string;
}

interface MarkIssueModalProps {
  /** e.g. "CLEOPATRA-03 · Egypt v1.4 · scene 3" or "Room-wide issue". */
  contextLine: string;
  onCancel: () => void;
  /** Must not throw — the parent handles success/failure feedback (toasts). */
  onSubmit: (data: MarkIssueSubmit) => Promise<void>;
}

/**
 * Custom modal for marking an operational issue — window.prompt is a silent
 * no-op in Electron, so all operator input goes through this. Styling clones
 * the Test Lab confirm overlay (fixed backdrop + centered white card).
 */
export default function MarkIssueModal({ contextLine, onCancel, onSubmit }: MarkIssueModalProps) {
  const [categories, setCategories] = useState<IssueCategory[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [severity, setSeverity] = useState<IssueSeverity>('warning');
  const [note, setNote] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getIssueCategories().then((cats) => {
      if (!cancelled) setCategories(cats);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = categories.find((c) => c.key === selectedKey) || null;

  const handleSubmit = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({ category: selected, severity, note: note.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#54626f',
    margin: '14px 0 6px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  };
  const fieldStyle: CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d5dce5',
    borderRadius: 8,
    fontSize: 14,
    background: '#fff',
    color: '#16202e',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(16,32,46,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={() => {
        if (!submitting) onCancel();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: 24,
          width: showKeyboard
            ? 'min(680px, calc(100vw - 24px))'
            : 'min(460px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - 24px)',
          overflowY: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
          color: '#16202e',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0 }}>Mark issue</h3>
        <div style={{ fontSize: 13, color: '#54626f', marginTop: 4 }}>{contextLine}</div>

        <label style={labelStyle}>Category *</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {categories.length === 0 && (
            <span style={{ color: '#8a97a6', fontSize: 13 }}>Loading categories…</span>
          )}
          {categories.map((cat) => {
            const isSel = cat.key === selectedKey;
            return (
              <button
                key={cat.key}
                onClick={() => setSelectedKey(cat.key)}
                disabled={submitting}
                style={{
                  padding: '7px 14px',
                  borderRadius: 100,
                  fontSize: 13,
                  cursor: 'pointer',
                  border: isSel ? '1px solid #2B6AC0' : '1px solid #d5dce5',
                  background: isSel ? '#2B6AC0' : '#fff',
                  color: isSel ? '#fff' : '#16202e',
                  fontWeight: isSel ? 600 : 400,
                }}
              >
                {cat.label}
              </button>
            );
          })}
        </div>

        <label style={labelStyle}>Severity</label>
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value as IssueSeverity)}
          disabled={submitting}
          style={fieldStyle}
        >
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="error">Error</option>
        </select>

        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <label style={labelStyle}>Note</label>
          <button
            type="button"
            className="refresh-btn"
            onClick={() => setShowKeyboard((v) => !v)}
            disabled={submitting}
            style={{ fontSize: 12, padding: '3px 10px', marginBottom: 6 }}
          >
            {showKeyboard ? 'Hide keyboard ⌄' : '⌨ Keyboard'}
          </button>
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onFocus={() => setShowKeyboard(true)}
          disabled={submitting}
          rows={3}
          placeholder="What happened? (optional)"
          style={{ ...fieldStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
        {showKeyboard && <TouchKeyboard value={note} onChange={setNote} disabled={submitting} />}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="refresh-btn" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button
            className="refresh-btn"
            onClick={handleSubmit}
            disabled={!selected || submitting}
            style={{
              background: '#2B6AC0',
              color: '#fff',
              fontWeight: 600,
              opacity: !selected || submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Marking…' : 'Mark issue'}
          </button>
        </div>
      </div>
    </div>
  );
}
